import {
  CURRENT_QUERY_BUILDER_VERSION,
  QueryBuilder,
  srs,
} from 'vet-bones/utils/queryBuilder';

import {
  ModelRule,
  ModelRuleType,
} from 'src/containers/ProjectDiagram/types/data';
import { getValidThenModel } from 'src/containers/ProjectDiagram/utils/rules/getValidThenModel';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { projectRuleModelSelector } from 'src/selectors/projectRuleModel';
import {
  Project,
  ProjectModel,
  ProjectModelRule,
  ProjectsState,
} from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';
import { RDFS_LABEL_IRI } from 'src/templates/queries/iris';
import { getAllProjectNamespaces } from 'src/utils/export/namespaces';

type OldQueryBuilderRule = {
  id: string;
  models: string[];
  name: string;
  ifClause: QueryBuilder;
  thenClause: QueryBuilder;
};

type OldTextRule = {
  id: string;
  models: string[];
  name: string;
  text: string;
};

type OldProjectModelRule =
  | Omit<OldQueryBuilderRule, 'models'>
  | Omit<OldTextRule, 'models'>;

type OldProjectModel = Omit<ProjectModel, 'rules'> & {
  rules: Record<string, OldProjectModelRule> | null;
};

type OldProject = Omit<Project, 'modelsById'> & {
  modelsById: Record<string, OldProjectModel>;
};

/**
 *  Migration to store consistent model rule
 */
// prevState must be typed as `any` since it's an older version of the Redux State
export const v41 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v41Projects(prevState.projects, prevState);
  }

  return nextState;
};

const v41Projects = (prevProjectsState: any, prevState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, OldProject>
  ).forEach(([projectId, project]) => {
    if (!project.diagram) {
      projectsById[projectId] = project as any;
      return;
    }

    const queryBuilderModel = getValidThenModel(
      projectRuleModelSelector(projectId)(prevState)
    );
    const namespaces = getAllProjectNamespaces(
      projectModelsByIdSelector(projectId)(prevState)
    );

    projectsById[projectId] = {
      ...project,
      modelsById: {
        ...Object.fromEntries(
          Object.entries(project.modelsById).map(([id, model]) => [
            id,
            {
              ...model,
              rules: model.rules
                ? Object.fromEntries(
                    Object.entries(model.rules).map<[string, ProjectModelRule]>(
                      ([ruleId, rule]) => {
                        if ('text' in rule) {
                          const parsed = srs(
                            rule.text || '',
                            queryBuilderModel,
                            namespaces,
                            { stardogLabelIri: RDFS_LABEL_IRI }
                          );
                          if (parsed?.rules.length === 1) {
                            return [
                              ruleId,
                              {
                                ...rule,
                                ifClause: {
                                  version: CURRENT_QUERY_BUILDER_VERSION,
                                  data: {
                                    query: parsed.rules[0].ifTopExpression,
                                    variableIds: [],
                                  },
                                },
                                thenClause: {
                                  version: CURRENT_QUERY_BUILDER_VERSION,
                                  data: {
                                    query: parsed.rules[0].thenTopExpression,
                                    variableIds: [],
                                  },
                                },
                                type: ModelRuleType.TEXT,
                              },
                            ];
                          }
                          return [
                            ruleId,
                            {
                              ...rule,
                              ifClause: null,
                              thenClause: null,
                              type: ModelRuleType.TEXT,
                            },
                          ];
                        }

                        return [
                          ruleId,
                          {
                            ...rule,
                            text: '',
                            type: ModelRuleType.QUERY_BUILDER,
                          },
                        ];
                      }
                    )
                  )
                : model.rules,
            },
          ])
        ),
      },
      diagram: {
        ...project.diagram,
        model: {
          ...project.diagram.model,
          modelData: {
            ...project.diagram.model.modelData,
            rulesById: Object.entries(
              project.diagram.model.modelData.rulesById as Record<
                string,
                OldQueryBuilderRule | OldTextRule
              >
            ).reduce<Record<string, ModelRule>>((acc, [ruleId, rule]) => {
              if ('text' in rule) {
                const parsed = srs(
                  rule.text || '',
                  queryBuilderModel,
                  namespaces,
                  { stardogLabelIri: RDFS_LABEL_IRI }
                );
                if (parsed?.rules.length === 1) {
                  acc[ruleId] = {
                    ...rule,
                    ifClause: {
                      version: CURRENT_QUERY_BUILDER_VERSION,
                      data: {
                        query: parsed.rules[0].ifTopExpression,
                        variableIds: [],
                      },
                    },
                    thenClause: {
                      version: CURRENT_QUERY_BUILDER_VERSION,
                      data: {
                        query: parsed.rules[0].thenTopExpression,
                        variableIds: [],
                      },
                    },
                    type: ModelRuleType.TEXT,
                  };
                } else {
                  acc[ruleId] = {
                    ...rule,
                    ifClause: null,
                    thenClause: null,
                    type: ModelRuleType.TEXT,
                  };
                }
              } else {
                acc[ruleId] = {
                  ...rule,
                  text: '',
                  type: ModelRuleType.QUERY_BUILDER,
                };
              }
              return acc;
            }, {}),
          },
        },
      },
    };
  });

  return { ...prevProjectsState, projectsById };
};
